import { dateParser } from '../utils/date-parser';
import { localProjectByProjectSubType, localProjectIdMapper } from '../tree-projects';
import { ImpactType } from '../impact/units';
import { TileVariant } from '../types/impact-order-item';
import { TileSource } from '../types/tiles';
import { TreeSupplyCompany } from '../types/tree-supply';
import type { ProjectSubType } from '@prisma/client';
import type { PublicProjectSummary } from '../projects';

export const NUMBER_OF_REFERRAL_TREES = 5;
export const LABEL_OF_REFERRAL_TREES = 'Ecologi (Referral Trees)';

// stolen from ReferralTreesDto in api
export type SubProfileTileType = {
  _id: string;
  username: string;
  name: string;
  firstName: string;
  lastName: string;
  createdAt: Date;
  isPrivate: boolean;
  totalTrees: number;
  userId?: string;
  source: TileSource;
  variant: TileVariant;
  projectTreeCounts: {
    projectId: number;
    treeCount: number;
  }[];
  groupId?: number;
};

export class SubProfile {
  _id: string;
  username: string;
  name: string;
  firstName: string;
  lastName: string;
  createdAt: Date;
  isPrivate: boolean;
  totalTrees: number;
  userId?: string;
  source: TileSource;
  variant: TileVariant;
  projectTreeCounts: {
    projectId: number;
    treeCount: number;
  }[];
  groupId?: number;

  constructor(subProfile: SubProfileTileType) {
    this._id = subProfile._id;
    this.username = subProfile.username;
    this.name = subProfile.name;
    this.firstName = subProfile.firstName;
    this.lastName = subProfile.lastName;
    this.createdAt = subProfile.createdAt;
    this.isPrivate = subProfile.isPrivate;
    this.userId = subProfile.userId;
    this.projectTreeCounts = subProfile.projectTreeCounts;
    this.totalTrees = subProfile.totalTrees;
    this.source = subProfile.source;
    this.variant = subProfile.variant;
    this.groupId = subProfile.groupId;
  }
}

export type TreeType = {
  _id: string;
  impactType: ImpactType;
  isPending?: boolean;
  callout?: string;
  createdAt: string;
  imageUrl?: string;
  isMocked?: boolean;
  latitude?: number;
  location?: string;
  longitude?: number;
  name?: string;
  plantedBy?: TreeSupplyCompany;
  plantedOnDate?: Date;
  projectId: number;
  projectSubType?: ProjectSubType | null;
  projectTitle?: string;
  species?: string;
  style: string;
  treeSupplyId?: string;
  value?: number;
  variant: TileVariant;
  source: TileSource;
  what3Words?: string;
  groupId?: number;
};

export class Tree {
  _id: string;
  callout?: string;
  createdAt: Date;
  imageUrl?: string;
  impactType: ImpactType;
  isPending?: boolean;
  isMocked: boolean;
  latitude?: number;
  location?: string;
  longitude?: number;
  name?: string;
  plantedBy?: TreeSupplyCompany;
  plantedOnDate?: Date;
  projectId: number;
  projectSubType?: ProjectSubType | null;
  projectTitle?: string;
  species?: string;
  style: string;
  treeSupplyId?: string;
  value: number;
  variant: TileVariant;
  source: TileSource;
  what3Words?: string;
  groupId?: number;

  constructor(tree: TreeType, project?: Pick<PublicProjectSummary, 'projectSubType'>) {
    this._id = tree._id; // Sometimes a Mongo ObjectId but acts like a string
    this.callout = tree.callout;
    this.createdAt = dateParser(tree.createdAt);
    this.imageUrl = tree.imageUrl || '';
    this.impactType = ImpactType.TREES;
    this.isPending = tree.isPending;
    this.isMocked = tree.isMocked == true;
    this.latitude = tree.latitude;
    this.location = tree.location;
    this.longitude = tree.longitude;
    this.name = tree.name;
    this.plantedBy = tree.plantedBy;
    this.plantedOnDate = tree.plantedOnDate ? dateParser(tree.plantedOnDate) : undefined;
    this.projectId = tree.projectId;
    this.projectSubType = project ? project.projectSubType : tree.projectSubType;
    this.projectTitle = tree.projectTitle;
    this.species = tree.species;
    this.style = tree.style;
    this.treeSupplyId = tree.treeSupplyId;
    this.value = tree.value || 1;
    this.variant = tree.variant;
    this.source = tree.source;
    this.what3Words = tree.what3Words;
    this.groupId = tree.groupId;
  }

  get isDividend() {
    return this.variant === TileVariant.DIVIDEND;
  }

  get isBirthday() {
    return this.variant === TileVariant.BIRTHDAY;
  }

  get treeYear() {
    return this.createdAt.toISOString().split('-')[0];
  }

  get isDonation() {
    return this.source === TileSource.DONATION;
  }

  get isApi() {
    return this.source === TileSource.API;
  }

  get localProject() {
    if (this.projectSubType) return localProjectByProjectSubType[this.projectSubType];
    if (this.projectSubType === null) return undefined;
    return localProjectIdMapper[this.projectId];
  }

  get what3WordsSplit() {
    return this.what3Words ? this.what3Words.split('.').join(' ') : '';
  }
}
